/**
 * Command bar dialog per spec section 15.12 and Claude Design Files
 * README section 5.14.
 *
 * Cmd-K (Ctrl-K) toggles. Esc closes. Three groups:
 *   1. Quick actions — create task / create lead / create opportunity /
 *      open settings / open today.
 *   2. Tasks — title-matching tasks with the type label on the right.
 *   3. Leads — company-or-contact-matching leads.
 *   4. Opportunities — name-or-company-matching opportunities.
 *
 * Up / down arrows navigate the flattened result list, Enter activates
 * the highlighted item, mouse hover updates the selection without
 * stealing the keyboard focus.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  Cog6ToothIcon,
  HomeIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';

import { useCommandBar } from '@/lib/command-bar';
import { useSlideOver } from '@/lib/slide-over';
import { useTasks, useCreateTask } from '@/lib/api/tasks';
import { useLeads, useOpportunities } from '@/lib/api/sales';
import { cn, todayIso } from '@/lib/utils';

interface Item {
  id: string;
  group: 'quick' | 'tasks' | 'leads' | 'opportunities';
  label: string;
  hint?: string;
  icon?: React.ReactNode;
  run: () => void;
}

export function CommandBar() {
  const isOpen = useCommandBar((s) => s.isOpen);
  const close = useCommandBar((s) => s.close);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);

  const navigate = useNavigate();
  const openSlideOver = useSlideOver((s) => s.open);
  const createTask = useCreateTask();

  const tasksQuery = useTasks();
  const leadsQuery = useLeads();
  const oppsQuery = useOpportunities();

  // Reset state when the dialog opens.
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setHighlight(0);
      const id = window.requestAnimationFrame(() => inputRef.current?.focus());
      return () => window.cancelAnimationFrame(id);
    }
    return undefined;
  }, [isOpen]);

  const items = useMemo<Item[]>(() => {
    const q = query.trim().toLowerCase();
    const out: Item[] = [];

    // Create task is special: when the user types a query it acts as a
    // create-with-this-title affordance, so the label reflects the query
    // verbatim and the action stays visible regardless of label match
    // (UAT 9.6). The remaining quick actions filter by label as before.
    const createTaskAction: Item = {
      id: 'quick-create-task',
      group: 'quick',
      label: q ? `Create task: "${query}"` : 'Create task',
      icon: <PlusIcon className="h-4 w-4" strokeWidth={1.6} aria-hidden />,
      run: () => {
        if (!q) {
          navigate({ to: '/tasks' });
          return;
        }
        // Round 6 PR #46: every create-task surface defaults the new
        // task's dueDate to today so the backend smart-default cascade
        // routes it into DOING (when WIP has space) or TODAY rather
        // than parking it on BACKLOG. QuickCapture already did this
        // since PR #35; the command bar now matches.
        createTask.mutate(
          { title: query, dueDate: todayIso() },
          { onSuccess: () => navigate({ to: '/tasks' }) },
        );
      },
    };

    const otherQuickActions: Item[] = [
      {
        id: 'quick-create-lead',
        group: 'quick',
        label: 'Create lead',
        icon: <PlusIcon className="h-4 w-4" strokeWidth={1.6} aria-hidden />,
        run: () => navigate({ to: '/sales/leads' }),
      },
      {
        id: 'quick-create-opp',
        group: 'quick',
        label: 'Create opportunity',
        icon: <PlusIcon className="h-4 w-4" strokeWidth={1.6} aria-hidden />,
        run: () => navigate({ to: '/sales/opportunities' }),
      },
      {
        id: 'quick-open-today',
        group: 'quick',
        label: 'Open today',
        icon: <HomeIcon className="h-4 w-4" strokeWidth={1.6} aria-hidden />,
        run: () => navigate({ to: '/' }),
      },
      {
        id: 'quick-open-settings',
        group: 'quick',
        label: 'Open settings',
        icon: <Cog6ToothIcon className="h-4 w-4" strokeWidth={1.6} aria-hidden />,
        run: () => navigate({ to: '/settings' }),
      },
    ];

    // Create task always leads the group when the user has a query —
    // pressing Enter on arbitrary text must create a task with that
    // title. Other quick actions still filter by label match.
    out.push(createTaskAction);
    out.push(
      ...otherQuickActions
        .filter((a) => (q ? a.label.toLowerCase().includes(q) : true))
        .slice(0, 4),
    );

    if (q) {
      const tasks = tasksQuery.data?.tasks ?? [];
      const matchingTasks = tasks
        .filter((t) => t.title.toLowerCase().includes(q))
        .slice(0, 8)
        .map<Item>((t) => ({
          id: `task-${t.id}`,
          group: 'tasks',
          label: t.title,
          hint: t.type,
          run: () => {
            openSlideOver({ kind: 'task', taskId: t.id });
            close();
          },
        }));
      out.push(...matchingTasks);

      const leads = leadsQuery.data?.leads ?? [];
      const matchingLeads = leads
        .filter(
          (l) =>
            l.companyName.toLowerCase().includes(q) ||
            (l.contactName ?? '').toLowerCase().includes(q),
        )
        .slice(0, 6)
        .map<Item>((l) => ({
          id: `lead-${l.id}`,
          group: 'leads',
          label: l.companyName,
          hint: l.contactName ?? l.stage,
          run: () => {
            openSlideOver({ kind: 'lead', leadId: l.id });
            close();
          },
        }));
      out.push(...matchingLeads);

      const opps = oppsQuery.data?.opportunities ?? [];
      const matchingOpps = opps
        .filter(
          (o) =>
            o.opportunityName.toLowerCase().includes(q) || o.companyName.toLowerCase().includes(q),
        )
        .slice(0, 6)
        .map<Item>((o) => ({
          id: `opp-${o.id}`,
          group: 'opportunities',
          label: `${o.companyName} · ${o.opportunityName}`,
          hint: o.stage,
          run: () => {
            openSlideOver({ kind: 'opportunity', opportunityId: o.id });
            close();
          },
        }));
      out.push(...matchingOpps);
    }

    return out;
  }, [
    query,
    tasksQuery.data,
    leadsQuery.data,
    oppsQuery.data,
    navigate,
    openSlideOver,
    close,
    createTask,
  ]);

  // Clamp highlight when items shrinks below the index.
  useEffect(() => {
    if (highlight >= items.length) setHighlight(Math.max(0, items.length - 1));
  }, [highlight, items.length]);

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(items.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[highlight];
      if (item) {
        item.run();
        close();
      }
    }
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={(next) => (next ? null : close())}>
      <Dialog.Portal>
        <Dialog.Overlay
          aria-hidden
          className="fixed inset-0 z-40 bg-black/55 data-[state=open]:animate-in data-[state=open]:fade-in"
        />
        <Dialog.Content
          aria-label="Command bar"
          onKeyDown={onKeyDown}
          className="fixed left-1/2 top-[14vh] z-50 w-[560px] max-w-[calc(100vw-2rem)] -translate-x-1/2 border border-mx-grey-800 bg-mx-card text-mx-white shadow-none"
        >
          <Dialog.Title className="sr-only">Command bar</Dialog.Title>
          <Dialog.Description className="sr-only">
            Search tasks, deals, or run a command. Press Escape to close.
          </Dialog.Description>

          <div className="flex items-center gap-mx-3 border-b border-mx-grey-800 px-mx-4 py-mx-3">
            <MagnifyingGlassIcon
              className="h-4 w-4 text-mx-grey-400"
              strokeWidth={1.6}
              aria-hidden
            />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setHighlight(0);
              }}
              placeholder="Search tasks, deals, or run a command…"
              className="flex-1 bg-transparent text-mx-body text-mx-white placeholder:text-mx-grey-500 focus:outline-none"
              aria-label="Search tasks, deals, or run a command"
            />
            <span className="mono-label text-mx-grey-mid">ESC</span>
          </div>

          <CommandList items={items} highlight={highlight} onHover={setHighlight} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function CommandList({
  items,
  highlight,
  onHover,
}: {
  items: Item[];
  highlight: number;
  onHover: (index: number) => void;
}) {
  if (items.length === 0) {
    return (
      <p className="px-mx-4 py-mx-4 text-mx-body-sm text-mx-grey-mid">
        No matches. Press Esc to close.
      </p>
    );
  }

  // Group the flat list back into sections for rendering.
  const grouped: Array<{ group: Item['group']; items: Item[] }> = [];
  for (const item of items) {
    const last = grouped[grouped.length - 1];
    if (last && last.group === item.group) {
      last.items.push(item);
    } else {
      grouped.push({ group: item.group, items: [item] });
    }
  }

  let index = 0;
  return (
    <div className="max-h-[60vh] overflow-y-auto py-mx-2">
      {grouped.map((g) => (
        <div key={g.group} className="flex flex-col">
          <p className="mono-label px-mx-4 pt-mx-2 text-mx-grey-mid">{GROUP_LABEL[g.group]}</p>
          <ul>
            {g.items.map((item) => {
              const i = index;
              index += 1;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={i === highlight}
                    onMouseEnter={() => onHover(i)}
                    onClick={() => item.run()}
                    className={cn(
                      'flex w-full items-center gap-mx-3 px-mx-4 py-mx-2 text-left text-mx-body-sm text-mx-grey-300 hover:text-mx-white',
                      i === highlight && 'bg-mx-grey-900 text-mx-white',
                    )}
                  >
                    {item.icon}
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.hint && <span className="mono-label text-mx-orange">{item.hint}</span>}
                    {i === highlight && (
                      <ChevronRightIcon
                        className="h-4 w-4 text-mx-orange"
                        strokeWidth={1.6}
                        aria-hidden
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

const GROUP_LABEL: Record<Item['group'], string> = {
  quick: 'QUICK ACTIONS',
  tasks: 'TASKS',
  leads: 'LEADS',
  opportunities: 'OPPORTUNITIES',
};
